import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store/definitions';
import {
  selectCurrentFacility,
  selectCurrentZone,
} from '../../store/zone/selector';
import styled, { useTheme } from 'styled-components';
import { useDemandContext } from './DemandContext';

import {
  ResponsiveContainer,
  ComposedChart,
  // Bar,
  // Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  // Cell,
  // TooltipProps,
} from 'recharts';
import { dataZip, numToSpacedString, isSpecialDay } from './GraphData';
import Spinner from 'components/Spinner';
import GraphLoader from 'components/Loader/graphLoader';
import {
  GraphData,
  GraphDataBundle,
  GTCompareKeys,
  GTransformer,
} from './defs';
import {
  selectCalcStepLengthMinutes,
  selectUiStateDataUpdateTS,
  selectUiStateDuration,
  selectUiStateEndDate,
  selectUiStatePriceTypeViewID,
  selectUIStateRevView,
  selectUiStateStartDate,
  selectUIValidPriceTypes,
} from 'store/ui/selector';
import { useAccRevContext } from 'containers/AccRevenueSticker';
import moment from 'moment';
import { addCharToNum } from 'util/range';
import { selectCurrentViewCompareDataSet } from 'store/compareCache/selector';
import { selectAllEventsFromCurrentFacility } from '../../store/event/selector';
import { ZoneFlag } from 'store/zone/reducer';
import { FacilityFlag } from 'store/uiSettings/reducer';
import { hasFlag } from 'util/facility';
import { selectCalcCacheData } from 'store/calcCache/selector';

const yAxisWidth = 80;

const Rotating = styled.span`
  width: 40px;
  height: 40px;
  text-align: center;
  animation-name: rotating;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;

  @keyframes rotating {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const MarginWrapper = styled.div`
  margin: 0px 0;

  margin-left: ${-yAxisWidth}px;
  margin-right: ${-yAxisWidth}px;

  position: relative;
`;

const AspectWrapOuter = styled.div`
  position: relative;
  width: 100%;
  padding-top: 40%;
`;
const AspectWrapInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const LoaderHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  //aspect-ratio: 2.5;
  height: 100%;

  font-size: 30px;
  font-weight: 500;
`;

const CustomLabel = styled.span`
  color: black;
  display: inline-block;
  font-size: 16px;
  margin-left: 5px;
  margin-right: 10px;
`;

export const TooltipWrapper = styled.div`
  padding: 10px;
  background: white;
  border: 1px solid #ccc;
`;

export const Payload = styled.div<{ color: string }>`
  margin-bottom: 5px;
  color: ${(props) => props.color};
  display: flex;
  justify-content: space-between;

  .first {
    display: inline-block;
    margin-right: 10px;
  }
`;

const GraphBase: FC<{ transformer: GTransformer; kind: string }> = ({
  transformer,
  kind,
}) => {
  const accRevContext = useAccRevContext(); // TODO: remove this hack
  const accDemandContext = useDemandContext();
  const theme = useTheme();

  const curZone = useSelector(selectCurrentZone);
  const curFacility = useSelector(selectCurrentFacility);
  //const events = useSelector((root: ApplicationState) => root.eventData);

  hasFlag(curZone, ZoneFlag.Electric);
  hasFlag(curFacility, FacilityFlag.NoFutureGraphDisplay);
  const events = useSelector(selectAllEventsFromCurrentFacility);
  const curSegment = useSelector(
    (root: ApplicationState) => root.uiState.segments
  );
  const startDate = useSelector(selectUiStateStartDate);
  const endDate = useSelector(selectUiStateEndDate);
  const cache = useSelector(selectCalcCacheData);
  const duration = useSelector(selectUiStateDuration);
  const curPriceViewTypeID = useSelector(selectUiStatePriceTypeViewID);
  const curRev = useSelector(selectUIStateRevView);
  const curCompCache = useSelector(selectCurrentViewCompareDataSet);

  const newCalcStep = useSelector(selectCalcStepLengthMinutes) / 60;
  const cachedPriceSeries = useSelector(selectUIValidPriceTypes);

  const hasCompare = curRev != 'regular';

  const compareDataKeys: GTCompareKeys =
    curRev === 'currProp'
      ? {
          indicateFuture: false,
          olderName: 'Current',
          olderValueKey: 'toRevenue',
          olderSegValueKey: 'oldRevenue',
          olderAccKey: 'toAccRev',
          newerName: 'Dynamic',
          newerValueKey: 'tnRevenue',
          newerSegValueKey: 'newRevenue',
          newerAccKey: 'tnAccRev',
        }
      : {
          indicateFuture: true,
          olderName: 'Previous year',
          olderValueKey: 'comRevenue',
          olderSegValueKey: 'oldRevenue', // TODO!?
          olderAccKey: 'comAccRev',
          newerName: 'Selected year',
          newerValueKey: 'tdRevenue',
          newerSegValueKey: 'defRevenue',
          newerAccKey: 'tdAccRev',
        };

  const uiDataTS = useSelector(selectUiStateDataUpdateTS);
  const cacheTS = cache?.cacheTS ?? 0;

  const [graphData, setGraphData] = useState<GraphData>(null as any);

  const getFirstWeekStep = (date: string) => {
    const year = date.substring(0, 4);
    const firstDay = new Date(year + '-01-01');
    const dayOfWeek = firstDay.getDay();
    switch (dayOfWeek) {
      case 0: {
        // Sunday
        return 24 * 1;
      }
      case 1: {
        // Monday
        return 0;
      }
      default: {
        return 24 * (7 - dayOfWeek + 1);
      }
    }
  };

  useEffect(() => {
    if (cache && cache!.data.zones) {
      const step = newCalcStep; //calcStep();
      let firstStep;
      switch (duration) {
        case 'weeksInYear': {
          const firstDateInData = cache!.data.zones[0].hours[0].time;
          firstStep = getFirstWeekStep(firstDateInData);
          break;
        }
        case 'month':
        case 'week':
        case 'day':
        default: {
          firstStep = step;
        }
      }

      const data = dataZip(
        cache!.data.zones,
        curSegment,
        curPriceViewTypeID,
        step,
        firstStep,
        curZone == null ? undefined : curZone.id,
        curCompCache
      );
      setGraphData(data);
    }
  }, [
    cache,
    newCalcStep,
    curSegment,
    curPriceViewTypeID,
    curZone,
    curRev,
    hasCompare ? curCompCache : 'regular',
  ]);

  if (curZone !== null && hasFlag(curZone, ZoneFlag.ForceNoGraph)) {
    return (
      <MarginWrapper>
        <AspectWrapOuter>
          <AspectWrapInner>
            <GraphLoader
              title={'Not enough data to make prediction'}
              forceError={true}
            />
          </AspectWrapInner>
        </AspectWrapOuter>
      </MarginWrapper>
    );
  }

  if (
    graphData === null ||
    !cache ||
    (cache.zone === null) !== (curZone === null) ||
    (cache?.zone && curZone && cache?.zone?.id !== curZone?.id) ||
    cache.startDate !== startDate ||
    cache.endDate !== endDate ||
    !cache.data.zones ||
    cache.data.zones.length === 0
  ) {
    const loader = (
      <MarginWrapper>
        <AspectWrapOuter>
          <AspectWrapInner>
            <GraphLoader />
          </AspectWrapInner>
        </AspectWrapOuter>
      </MarginWrapper>
    );
    // console.log(
    //   `GDNULL:${graphData === null} Cache:${cache} CaZon:${cache?.zone} (${
    //     cache?.zoneIdSet
    //   })  CuZon:${curZone} Non-matchingZID:${
    //     cache?.zone && curZone && cache?.zone?.id !== curZone?.id
    //   }`,
    //   loader
    // );
    return loader;
  }
  //return <Wrap>Graph placeholder</Wrap>;

  // Hacky way to move state upwards in component stack, please refactor when there's more time.
  accRevContext.setVisibleDiff(hasCompare);
  accRevContext.setAccRev(
    (graphData.data[graphData.data.length - 1]?.[compareDataKeys.newerAccKey] ??
      -1) as number
  );
  accRevContext.setOldAccRev(
    (hasCompare
      ? graphData.data[graphData.data.length - 1]?.[
          compareDataKeys.olderAccKey
        ] ?? -1
      : graphData.data[graphData.data.length - 1]?.[
          compareDataKeys.newerAccKey
        ] ?? -1) as number
  );

  accDemandContext.setAccDemand({
    currentAcc: graphData.demandAcc,
    oldAcc: graphData.oldDemandAcc,
    diff: graphData.custAccDiff,
  });

  const getXAxisInterval = (view: string, duration: string) => {
    switch (duration) {
      case 'day': {
        return 23;
      }
      case 'week': {
        if (newCalcStep === 1) return 23;
        else return 0;
      }
      case 'month': {
        return 1;
      }
    }
    return 0;
  };

  const xAxisInterval = getXAxisInterval(transformer.name, duration);

  const tooltipLabelFormatter =
    newCalcStep < 24
      ? (value: string) => moment(value).format('yyyy-MM-DD, HH:00') // value string should include time
      : (value: string) => moment(value).format('yyyy-MM-DD'); // exclude time

  const cpsPT = cachedPriceSeries.find((cps) => cps.id === curPriceViewTypeID);

  var _graphData = {
    ...graphData,
    priceType: cpsPT
      ? cpsPT.name + (cpsPT.isAverage ? ' avg' : '')
      : curPriceViewTypeID,
    revenueType: curRev,
    zone: curZone,
    facility: curFacility,
    events,
  } as GraphDataBundle;

  var sum = graphData.data.reduce((acc, curr) => {
    if (kind === 'revenue') return (acc += curr.tdRevenue);
    else return (acc += curr.demand);
  }, 0);

  if (curZone !== null && hasFlag(curZone, ZoneFlag.LowData) && sum === 0) {
    return (
      <MarginWrapper>
        <AspectWrapOuter>
          <AspectWrapInner>
            <GraphLoader
              title={'Not enough data to make prediction'}
              forceError={true}
            />
          </AspectWrapInner>
        </AspectWrapOuter>
      </MarginWrapper>
    );
  }

  const renderTooltip = (props: any) => {
    const specialDay = isSpecialDay(
      moment.utc(props.label).toDate(),
      _graphData
    );
    return (
      <TooltipWrapper>
        <label>{tooltipLabelFormatter(props.label)}</label>
        {props &&
          props.payload &&
          props.payload.map((x: any) => {
            var valueNumber = Number(x.value);

            if (
              x.payload.tempDisabled &&
              -1 == x.name.toLowerCase().indexOf('prices')
            ) {
              return (
                <Payload color={x.color}>
                  <span className={'first'}>{x.name}:</span>
                  <span>Prediction is temporary disabled</span>
                </Payload>
              );
            }
            if (!isNaN(valueNumber)) {
              return (
                <Payload color={x.color} key={x.name}>
                  <span className={'first'}>{x.name}:</span>
                  <span>
                    {addCharToNum(
                      (Math.round(Number(valueNumber) * 10) / 10).toFixed(0),
                      ' '
                    )}
                  </span>
                </Payload>
              );
            }
            return (
              <Payload color={x.color}>
                <span className={'first'}>{x.name}:</span>
                <span>{x.value}</span>
              </Payload>
            );
          })}
        {specialDay.eventExists &&
          specialDay.event!.map((x) => (
            <Payload key={x.dateID} color={'#000'}>
              <span className="first">Event:</span>
              <span>{x.name}</span>
            </Payload>
          ))}
      </TooltipWrapper>
    );
  };

  if (
    hasFlag(curFacility, FacilityFlag.NoFutureGraphDisplay) ||
    (!!curZone && hasFlag(curZone, ZoneFlag.LowData))
  ) {
    var tomorrow = moment
      .utc()
      .startOf('day')
      .add(1, 'day')
      .format('YYYY-MM-DD');
    var iterationValue = {
      defRev: 0,
      newRev: 0,
      oldRev: 0,
      tdRev: 0,
      tnRev: 0,
      toRev: 0,
      demand: 0,
    };
    graphData.data = graphData.data.map((f) => {
      if (f.date >= tomorrow) {
        iterationValue.demand += f.demand;
        return {
          ...f,
          tdAccRev: iterationValue.tdRev,
          tdRevenue: 0,
          tnAccRev: iterationValue.tnRev,
          tnRevenue: 0,
          toAccRev: iterationValue.toRev,
          toRevenue: 0,
          demand: 0,
          //price: 0,
          tempDisabled: true,
          segments: f.segments.map((seg) => {
            return {
              ...seg,
              demand: 0,
              defRevenue: {
                current: 0,
                accumulated: iterationValue.defRev,
              },
              newRevenue: {
                current: 0,
                accumulated: iterationValue.newRev,
              },
              oldRevenue: {
                current: 0,
                accumulated: iterationValue.oldRev,
              },
            };
          }),
        };
      }
      iterationValue.tdRev = f.tdAccRev;
      iterationValue.tnRev = f.tnAccRev;
      iterationValue.toRev = f.toAccRev;
      f.segments.map((f) => {
        iterationValue.defRev = f.defRevenue.accumulated;
        iterationValue.newRev = f.newRevenue.accumulated;
        iterationValue.oldRev = f.oldRevenue.accumulated;
      });
      return f;
    });
    graphData.demandAcc -= iterationValue.demand;
    _graphData.demandAcc = graphData.demandAcc;
    _graphData.data = graphData.data;
  }

  const { chartData, leftMaxViewValue, rightMaxViewValue } = transformer(
    _graphData,
    curZone !== undefined,
    compareDataKeys
  );

  let prevDrawnMonthTick = -1000;
  let prevDrawnDayTick = -1000;

  // console.log(`UITS:${uiDataTS} CCTS:${cacheTS}`);

  return (
    <MarginWrapper>
      <ResponsiveContainer width="100%" height={600}>
        <ComposedChart
          margin={{
            top: 20,
            right: 0,
            left: 0,
            bottom: 0,
          }}
          data={graphData.data}
        >
          <CartesianGrid stroke={theme.palette.common.lightBorder} />
          <XAxis
            textAnchor="start"
            scale="band"
            dataKey="date"
            tickFormatter={(v) => {
              const date = new Date(v);
              if (duration === 'weeksInYear') {
                if (date.getMonth() === prevDrawnMonthTick) {
                  return '';
                }
                prevDrawnMonthTick = date.getMonth();
              }
              return `${date.getDate()}/${date.getMonth() + 1}`;
            }}
            tick={{
              fill: theme.palette.text.fg,
              textAnchor: 'middle',
            }}
            stroke={theme.palette.common.lightBorder}
            interval={xAxisInterval}
          />
          <YAxis
            domain={[0, leftMaxViewValue]}
            yAxisId="left"
            orientation="left"
            tickFormatter={numToSpacedString}
            width={yAxisWidth}
            tick={{ fill: theme.palette.text.fg }}
            stroke={theme.palette.common.lightBorder}
          />
          <YAxis
            domain={[0, rightMaxViewValue]}
            yAxisId="right"
            orientation="right"
            tickFormatter={numToSpacedString}
            width={yAxisWidth}
            tick={{ fill: theme.palette.text.fg }}
            stroke={theme.palette.common.lightBorder}
          />
          {chartData}
          <Tooltip
            // labelFormatter={tooltipLabelFormatter}
            // formatter={(value: string, name: string, props: any) => {
            //   var valueNumber = Number(value);
            //   if (!isNaN(valueNumber)) {
            //     return (Math.round(Number(valueNumber) * 10) / 10).toString();
            //   }
            //   return value;
            // }}
            content={renderTooltip}
          />
          <Legend
            formatter={(value, entry, index) => (
              <CustomLabel>{value}</CustomLabel>
            )}
          />
        </ComposedChart>
      </ResponsiveContainer>
      {uiDataTS != cacheTS && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#ffffff',
            opacity: 0.35,
          }}
        >
          <GraphLoader />
        </div>
      )}
    </MarginWrapper>
  );
};

export default GraphBase;
